import React, { useState, useEffect, useContext } from "react";
import { Layout, Menu, message, Modal } from "antd";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  DashboardFilled,
  CarFilled,
  ShopFilled,
  PayCircleFilled,
  CreditCardFilled,
  BarChartOutlined,
  AlertFilled,
  ToolFilled,
  SettingFilled,
  ControlOutlined,
  AuditOutlined,
  QrcodeOutlined,
  CreditCardOutlined,
  WalletOutlined,
} from "@ant-design/icons";
import logo from "../../Assets/Images/logo.svg";
import logout from "../../Assets/Images/logout.svg";
import { UserContext } from "../../App";
import { handleLogout, getRefreshToken } from "../../Utlis/UpdateUserState";
import { logout as logoutApi } from "../../API/auth";
import { getAdminProfile } from "../../API/Admin/MyProfile";
import { getApprovalsRequests } from "../../API/Admin/Approval&Requests";
import useProfileSync from "../../Utlis/useProfileSync";

import "./style.css";
import InitialsAvatar from "../InitialsAvatar";

const { Sider } = Layout;

const BACKEND_ORIGIN = process.env.REACT_APP_BACKEND_URL?.replace(
  /\/?api\/?$/,
  "",
);

const resolveAvatarUrl = (avatar) => {
  if (!avatar) return null;
  if (/^https?:\/\//i.test(avatar) || avatar.startsWith("blob:")) {
    return avatar;
  }
  return `${BACKEND_ORIGIN}${avatar.startsWith("/") ? "" : "/"}${avatar}`;
};

const SidebarAdmin = ({
  collapsed,
  setCollapsed,
  breakpoint,
  user,
  setUser,
}) => {
  const { userData, setUserData, setUserToken } = useContext(UserContext);
  const location = useLocation();
  const [selectedId, setSelectedId] = useState("1");
  const navigate = useNavigate();
  const pageAccess = userData?.page_access || {};
  const [profileInfo, setProfileInfo] = useState({ name: "", role: "", avatar: null });

  useProfileSync(getAdminProfile, (data) => {
    setProfileInfo({
      name: data.name || "",
      role: data.role || "",
      avatar: resolveAvatarUrl(data.avatar),
    });
  });

  useEffect(() => {
    // Reflect avatar changes made on the Profile page instantly, without
    // waiting for a remount/refetch of this sidebar.
    if (!userData?.avatar) return;

    setProfileInfo((prev) => ({
      ...prev,
      avatar: resolveAvatarUrl(userData.avatar),
    }));
  }, [userData?.avatar]);

  useEffect(() => {
    // Reflect name changes made on the Profile page instantly too.
    if (!userData?.name) return;

    setProfileInfo((prev) => ({
      ...prev,
      name: userData.name,
    }));
  }, [userData?.name]);

  useEffect(() => {
    // Reflect role changes made on the Profile page instantly too.
    if (!userData?.role) return;

    setProfileInfo((prev) => ({
      ...prev,
      role: userData.role,
    }));
  }, [userData?.role]);

  const pageAccessSlugMap = {
    "1": "dashboard",
    "2": "fleet-management",
    "3": "subscription-payments",
    "4": "fuel-vendors",
    "5": "system-configuration",
    "6": "transactions",
    "7": "credits-billing",
    "8": "reports-analytics",
    "9": "approvals-requests",
    "10": "qr-management",
    "11": "user-management",
    "12": "settings-permissions",
    "13": "audit-trail",
  };

  const isPageVisible = (key) => pageAccess[pageAccessSlugMap[key]] !== "restrict";

  const [pendingApprovals, setPendingApprovals] = useState(0);
  const canSeeApprovals = isPageVisible("9");

  useEffect(() => {
    if (!canSeeApprovals) return;
    let active = true;

    const fetchPendingApprovals = async () => {
      try {
        const res = await getApprovalsRequests();
        const results = res?.data?.data?.results;
        if (!active || Math.floor(res?.status / 100) !== 2) return;
        if (!Array.isArray(results)) return;
        setPendingApprovals(
          results.filter(
            (item) => (item.status || "pending").toLowerCase() === "pending",
          ).length,
        );
      } catch (err) {
        console.error("Failed to load pending approvals:", err);
      }
    };

    fetchPendingApprovals();
    return () => {
      active = false;
    };
  }, [location.pathname, canSeeApprovals]);

  const routeKeyMap = {
    "/dashboard-admin": "1",
    "/fleet-management": "2",
    "/subscription-payments-admin": "3",
    "/fuel-vendors-admin": "4",
    "/system-configuration": "5",
    "/transactions-admin": "6",
    "/credits-billings-admin": "7",
    "/reports-analytics": "8",
    "/approvals-requests": "9",
    "/qr-management": "10",
    "/user-management-admin": "11",
    "/settings-permissions-admin": "12",
    "/audit-trail": "13",
  };

  useEffect(() => {
    const path = location.pathname;

    // Find matching route
    const matchedRoute = Object.keys(routeKeyMap).find((route) =>
      path.startsWith(route),
    );

    if (matchedRoute) {
      setSelectedId(routeKeyMap[matchedRoute]);
    } else if (path.startsWith("/profile-admin")) {
      // Profile isn't a menu page, so nothing should appear selected
      setSelectedId("");
    }
  }, [location.pathname]);

  const items = [
    {
      type: "group",
      label: "All Pages",
      children: [
        {
          key: "1",
          icon: <DashboardFilled />,
          label: <Link to="/dashboard-admin">Dashboard</Link>,
        },
        {
          key: "2",
          icon: <CarFilled />,
          label: <Link to="/fleet-management">Fleet Management</Link>,
        },
        {
          key: "3",
          icon: <WalletOutlined />,
          label: (
            <Link to="/subscription-payments-admin">Subscription Payments</Link>
          ),
        },
        {
          key: "4",
          icon: <ShopFilled />,
          label: <Link to="/fuel-vendors-admin">Fuel Vendors</Link>,
        },
        {
          key: "5",
          icon: <ControlOutlined />,
          label: <Link to="/system-configuration">System Configuration</Link>,
        },
        {
          key: "6",
          icon: <PayCircleFilled />,
          label: <Link to="/transactions-admin">Transactions</Link>,
        },
        {
          key: "7",
          icon: <CreditCardFilled />,
          label: <Link to="/credits-billings-admin">Credits & Billing</Link>,
        },
        {
          key: "8",
          icon: <BarChartOutlined />,
          label: <Link to="/reports-analytics">Reports & Analytics</Link>,
        },
        {
          key: "9",
          icon: (
            <span className="menu-icon-with-count">
              <AlertFilled />
              {pendingApprovals > 0 && (
                <sup className="menu-count-badge">
                  {pendingApprovals > 99 ? "99+" : pendingApprovals}
                </sup>
              )}
            </span>
          ),
          label: <Link to="/approvals-requests">Approvals & Requests</Link>,
        },
        {
          key: "10",
          icon: <QrcodeOutlined />,
          label: <Link to="/qr-management">QR Management</Link>,
        },
        {
          key: "11",
          icon: <ToolFilled />,
          label: <Link to="/user-management-admin">User Management</Link>,
        },
        {
          key: "12",
          icon: <SettingFilled />,
          label: (
            <Link to="/settings-permissions-admin">Settings & Permissions</Link>
          ),
        },
        {
          key: "13",
          icon: <AuditOutlined />,
          label: <Link to="/audit-trail">Audit Trail</Link>,
        },
      ].filter((item) => isPageVisible(item.key)),
    },
  ];

  const handleProfileClick = () => {
    navigate("/profile-admin");
  };

  return (
    <Sider
      className="sidebar"
      width={240}
      collapsible
      collapsed={collapsed}
      onCollapse={(val) => setCollapsed(val)}
      collapsedWidth={80}
      breakpoint="lg"
      onBreakpoint={(broken) => {
        setCollapsed(broken);
      }}
      theme="light"
      style={{ height: "100vh", position: "fixed" }}
    >
      <div className={`logo-container ${collapsed ? "collapsed" : ""}`}>
        <img src={logo} alt="logo" style={{ width: "143px" }} />
      </div>
      <div className="sidebar-divider"></div>
      <div
        style={{
          height: "90vh",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
        }}
      >
        <Menu
          mode="inline"
          theme="light"
          selectedKeys={[selectedId]}
          items={items}
        />
        <div className={`logout-div ${collapsed ? "collapsed" : ""}`}>
          <div className="logout-user" onClick={handleProfileClick}>
            <InitialsAvatar
              className="profile-pic"
              src={profileInfo.avatar}
              name={profileInfo.name}
              alt={profileInfo.name || "Profile"}
              style={{ fontSize: 14 }}
            />

            <div className="logout-user-details">
              <div className="name">{profileInfo.name || "-"}</div>
              <div className="role">{profileInfo.role || "-"}</div>
            </div>

            <div className="logout-icon">
              <img src={logout} alt="logout Icon" />
            </div>
          </div>
        </div>
      </div>
    </Sider>
  );
};

export default SidebarAdmin;
